import posthog from "posthog-js";
import { config } from "../config";
import { consumeAnalyticsHandoff } from "./handoff";

let ready = false;

/** Replace raw linkId path/query segments so they never land in PostHog URLs. */
export function sanitizeLinkIdInUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    if (u.searchParams.has("linkId")) {
      u.searchParams.set("linkId", ":linkId");
    }
    // Path style /:linkId/... — first segment that looks like a link id
    const parts = u.pathname.split("/");
    if (parts.length >= 2 && parts[1] && /^[A-Za-z0-9_-]{8,}$/.test(parts[1])) {
      // Keep known app routes intact; only sanitize opaque ids
      const known = new Set([
        "home",
        "welcome-page",
        "transfer-page",
        "cip",
        "cip-page",
        "error-page",
        "support",
        "legal",
        "transactions-page",
        "sending-money-page",
        "cash-deposit",
        "add-card",
        "receipt",
      ]);
      if (!known.has(parts[1])) {
        parts[1] = ":linkId";
        u.pathname = parts.join("/");
      }
    }
    return u.toString();
  } catch {
    return url.replace(/linkId=[^&#]+/gi, "linkId=:linkId");
  }
}

export function initAnalytics(): void {
  const key = config.POSTHOG_KEY?.trim();
  if (!key) return;
  try {
    const handoff = consumeAnalyticsHandoff();
    posthog.init(key, {
      api_host: config.POSTHOG_HOST || "https://us.i.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: false,
      autocapture: false,
      capture_exceptions: true,
      ...(handoff
        ? {
            bootstrap: {
              sessionID: handoff.sessionId,
              distinctID: handoff.distinctId,
              isIdentifiedID: true,
            },
          }
        : {}),
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",
      },
      sanitize_properties: (props) => {
        if (typeof props.$current_url === "string") {
          props.$current_url = sanitizeLinkIdInUrl(props.$current_url);
        }
        if (typeof props.$pathname === "string") {
          props.$pathname = sanitizeLinkIdInUrl(props.$pathname);
        }
        return props;
      },
      loaded: (ph) => {
        ph.register({
          service: config.SERVICE_NAME,
          environment: config.ENV_NAME || "unknown",
        });
      },
    });
    ready = true;
  } catch (e) {
    console.warn("[analytics] PostHog init failed", e);
  }
}

export function safeCapture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!ready) return;
  try {
    posthog.capture(event, properties);
  } catch {
    /* never break UX */
  }
}

export function safeCaptureException(
  error: unknown,
  properties?: Record<string, unknown>,
): void {
  if (!ready) return;
  try {
    posthog.captureException(error, properties);
  } catch {
    /* never break UX */
  }
}

export function safeIdentify(
  distinctId: string,
  properties?: Record<string, unknown>,
): void {
  if (!ready) return;
  try {
    posthog.identify(distinctId, properties);
  } catch {
    /* ignore */
  }
}

export function safeRegister(properties: Record<string, unknown>): void {
  if (!ready) return;
  try {
    posthog.register(properties);
  } catch {
    /* ignore */
  }
}

export function safeGroup(
  groupType: string,
  groupKey: string,
  properties?: Record<string, unknown>,
): void {
  if (!ready) return;
  try {
    posthog.group(groupType, groupKey, properties);
  } catch {
    /* ignore */
  }
}

export function resetAnalytics(): void {
  if (!ready) return;
  try {
    posthog.reset();
  } catch {
    /* ignore */
  }
}

export function getAnalyticsSession(): {
  distinctId: string | undefined;
  sessionId: string | undefined;
  windowId: string | undefined;
} {
  if (!ready) {
    return { distinctId: undefined, sessionId: undefined, windowId: undefined };
  }
  try {
    return {
      distinctId: posthog.get_distinct_id?.(),
      sessionId: posthog.get_session_id?.(),
      windowId: undefined,
    };
  } catch {
    return { distinctId: undefined, sessionId: undefined, windowId: undefined };
  }
}

export function isAnalyticsReady(): boolean {
  return ready;
}
